import type {
  OrderProductRealizationDto,
  RouteOrderDto,
  TransportProductDto,
  TransportRouteSummaryDto,
  TransportOrderSummaryDto,
} from '../../types/transport';

/** One linked customer order on one physical truck (grouped list view). */
export interface TransportTruckOrder {
  orderId: string;
  order: TransportOrderSummaryDto;
  routeOrder: RouteOrderDto;
  productRealization: OrderProductRealizationDto[];
  /** Cargo of THIS order on THIS truck. */
  products: TransportProductDto[];
}

/**
 * One row of the grouped list = one transport = one physical truck. The money
 * (truck rate) belongs to the truck, so it must appear once per truck, never
 * once per linked order (owner review 2026-10-10: a truck with two ZK looked
 * like two trucks with the full rate each).
 */
export interface TransportTruckGroup {
  transport: TransportRouteSummaryDto;
  orders: TransportTruckOrder[];
}

export interface TransportTruckGrouping {
  customerTrucks: TransportTruckGroup[];
  supplierOnlyTransports: TransportRouteSummaryDto[];
  /** Distinct customer orders across the whole list. */
  orderCount: number;
}

function getCanonicalOrderId(routeOrder: RouteOrderDto): string {
  return routeOrder.order.id || routeOrder.orderId;
}

function mergeCargoProduct(
  cargoItem: TransportRouteSummaryDto['cargoItems'][number],
  orderProduct?: TransportProductDto,
): TransportProductDto {
  return {
    ...(orderProduct || {}),
    id: cargoItem.orderProductId || cargoItem.id,
    cargoItemId: cargoItem.id,
    orderId: cargoItem.orderId,
    name: cargoItem.productName || orderProduct?.name || 'Produkt',
    quantity: cargoItem.quantity,
    unit: cargoItem.unit || orderProduct?.unit,
    grossWeight: cargoItem.grossWeight ?? orderProduct?.grossWeight,
    width: cargoItem.width ?? orderProduct?.width,
    height: cargoItem.height ?? orderProduct?.height,
    length: cargoItem.length ?? orderProduct?.length,
    thickness: cargoItem.thickness ?? orderProduct?.thickness,
  };
}

export function getTransportProductsForOrder(
  transport: TransportRouteSummaryDto,
  routeOrder: RouteOrderDto,
): TransportProductDto[] {
  const canonicalOrderId = getCanonicalOrderId(routeOrder);
  const cargoItems = transport.cargoItems.filter((item) => item.orderId === canonicalOrderId);
  if (cargoItems.length === 0) {
    // Legacy order link without explicit cargo is interpreted as a full-order
    // reservation by the backend. Show the same product set on the truck row.
    return routeOrder.order.products;
  }

  const orderProductsById = new Map(
    routeOrder.order.products.map((product) => [product.id, product]),
  );
  return cargoItems.map((cargoItem) => mergeCargoProduct(
    cargoItem,
    cargoItem.orderProductId ? orderProductsById.get(cargoItem.orderProductId) : undefined,
  ));
}

export function buildTransportTruckGrouping(
  transports: TransportRouteSummaryDto[],
): TransportTruckGrouping {
  // Realization is a global server snapshot over every active transport, not
  // a value that can be reconstructed from the currently filtered list. Take
  // the first non-empty snapshot per order; if the API omits it everywhere,
  // keep it empty so a contract break is visible instead of displaying a
  // plausible but filter-dependent balance.
  const realizationByOrderId = new Map<string, OrderProductRealizationDto[]>();
  transports.forEach((transport) => {
    transport.routeOrders.forEach((routeOrder) => {
      const orderId = getCanonicalOrderId(routeOrder);
      if (!realizationByOrderId.get(orderId)?.length) {
        realizationByOrderId.set(orderId, routeOrder.productRealization);
      }
    });
  });

  const customerTrucks: TransportTruckGroup[] = [];
  const supplierOnlyTransports: TransportRouteSummaryDto[] = [];

  transports.forEach((transport) => {
    if (transport.routeOrders.length === 0) {
      supplierOnlyTransports.push(transport);
      return;
    }

    const orders: TransportTruckOrder[] = [];
    transport.routeOrders.forEach((routeOrder) => {
      // The nested order is the canonical entity. `routeOrder.orderId` comes
      // from the allocation projection and older/global-create responses may
      // carry a stale alias there.
      const orderId = getCanonicalOrderId(routeOrder);
      if (orders.some((entry) => entry.orderId === orderId)) return;
      orders.push({
        orderId,
        order: routeOrder.order,
        routeOrder,
        productRealization: realizationByOrderId.get(orderId) || [],
        products: getTransportProductsForOrder(transport, routeOrder),
      });
    });
    customerTrucks.push({ transport, orders });
  });

  return {
    customerTrucks,
    supplierOnlyTransports,
    orderCount: realizationByOrderId.size,
  };
}
